/** @deprecated 旧版节点模式组件，已被 GenerateNode + CardContentRenderer 统一架构替代。请勿在此添加新功能 */
import React, { useState, memo } from 'react';
import { AppNode, NodeStatus } from '@/types';
import { NodeHeader, InputPorts, OutputPorts, ModelSelector, PromptInput, GenerateButton, MediaPreview } from '../parts';
import { FileSearch } from 'lucide-react';

interface AnalyzeModeProps {
  node: AppNode;
  onUpdate: (id: string, data: Partial<AppNode['data']>) => void;
  onAction: (id: string, prompt?: string) => void;
  onDelete: (id: string) => void;
  isConnecting?: boolean;
  onPortMouseDown: (e: React.MouseEvent, id: string, type: 'input' | 'output') => void;
  onPortMouseUp: (e: React.MouseEvent, id: string, type: 'input' | 'output') => void;
  inputAssets?: { id: string; type: 'image' | 'video' | 'audio'; src: string }[];
}

const AnalyzeModeInner: React.FC<AnalyzeModeProps> = ({
  node,
  onUpdate,
  onAction,
  onDelete,
  isConnecting,
  onPortMouseDown,
  onPortMouseUp,
  inputAssets = [],
}) => {
  const [prompt, setPrompt] = useState(node.data.prompt || '');
  const isWorking = node.status === NodeStatus.WORKING;

  const handlePromptChange = (value: string) => {
    setPrompt(value);
    onUpdate(node.id, { prompt: value });
  };

  const handleAnalyze = () => {
    onAction(node.id, prompt);
  };

  const handleModelChange = (modelId: string) => {
    onUpdate(node.id, { model: modelId });
  };

  return (
    <div className="relative">
      <NodeHeader
        node={node}
        onUpdate={onUpdate}
        onDelete={onDelete}
      />
      
      <InputPorts
        node={node}
        isConnecting={isConnecting}
        onPortMouseDown={onPortMouseDown}
        onPortMouseUp={onPortMouseUp}
      />
      
      <div className="p-3 flex flex-col gap-2">
        {inputAssets.length > 0 && (
          <div className="flex gap-1 mb-2">
            {inputAssets.slice(0, 4).map((asset, i) => (
              <div key={asset.id} className="w-12 h-12 rounded overflow-hidden border border-white/20">
                {asset.type === 'video' ? (
                  <video src={asset.src} className="w-full h-full object-cover" />
                ) : (
                  <img src={asset.src} className="w-full h-full object-cover" alt={`输入 ${i + 1}`} />
                )}
              </div>
            ))}
          </div>
        )}
        
        {node.data.analysis && (
          <div className="bg-black/20 rounded-lg p-2 text-xs text-slate-300 max-h-32 overflow-y-auto custom-scrollbar">
            {node.data.analysis}
          </div>
        )}
        
        <PromptInput
          value={prompt}
          placeholder="描述您想分析的内�?.."
          onChange={handlePromptChange}
          onSubmit={handleAnalyze}
          disabled={isWorking}
        />
        
        <div className="flex items-center justify-between">
          <ModelSelector
            mode="analyze"
            selectedModel={node.data.model || ''}
            onChange={handleModelChange}
            disabled={isWorking}
          />
          
          <button
            onClick={handleAnalyze}
            disabled={isWorking}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-[12px] font-bold text-[10px] tracking-wide transition-all duration-300 ${
              isWorking
                ? 'bg-white/5 text-slate-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-500 to-pink-500 text-black hover:shadow-lg hover:shadow-purple-500/20 hover:scale-105 active:scale-95'
            }`}
          >
            {isWorking ? (
              <>
                <FileSearch className="animate-pulse" size={12} />
                <span>分析�?..</span>
              </>
            ) : (
              <>
                <FileSearch size={12} />
                <span>分析</span>
              </>
            )}
          </button>
        </div>
      </div>
      
      <OutputPorts
        node={node}
        isConnecting={isConnecting}
        onPortMouseDown={onPortMouseDown}
        onPortMouseUp={onPortMouseUp}
      />
    </div>
  );
};

export const AnalyzeMode = memo(AnalyzeModeInner, (prevProps, nextProps) => {
  return (
    prevProps.node.id === nextProps.node.id &&
    prevProps.node.status === nextProps.node.status &&
    prevProps.node.data?.model === nextProps.node.data?.model &&
    prevProps.node.data?.analysis === nextProps.node.data?.analysis &&
    prevProps.node.data?.prompt === nextProps.node.data?.prompt &&
    prevProps.node.data?.error === nextProps.node.data?.error &&
    prevProps.isConnecting === nextProps.isConnecting
  );
});